import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { ImageUpload } from "./ImageUpload";
import {
  useCreateCameraPlan,
  useUploadCameraPlanImage,
} from "@/hooks/useCameraPlans";

interface CreateCameraPlanDialogProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (id: string) => void;
}

export function CreateCameraPlanDialog({
  projectId,
  open,
  onOpenChange,
  onCreated,
}: CreateCameraPlanDialogProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [mapType, setMapType] = useState<"image" | "embed">("image");
  const [eventDate, setEventDate] = useState("");
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [embedUrl, setEmbedUrl] = useState("");

  const createPlan = useCreateCameraPlan();
  const uploadImage = useUploadCameraPlanImage();

  const isSubmitting = createPlan.isPending || uploadImage.isPending;

  const handleSubmit = async () => {
    if (!name.trim()) return;

    try {
      const plan = await createPlan.mutateAsync({
        projectID: projectId,
        name: name.trim(),
        description: description.trim() || null,
        mapType: mapType,
        imageURL: null,
        embedURL: mapType === "embed" ? embedUrl.trim() || null : null,
        mapCenterLng: null,
        mapCenterLat: null,
        mapZoom: null,
        eventDate: eventDate || null,
        sortOrder: 0,
      });

      if (pendingFile && mapType === "image") {
        await uploadImage.mutateAsync({
          cameraPlanId: plan.id,
          projectId,
          file: pendingFile,
        });
      }

      resetForm();
      onOpenChange(false);
      onCreated?.(plan.id);
    } catch {
      // Error handled by mutation hooks
    }
  };

  const resetForm = () => {
    setName("");
    setDescription("");
    setEventDate("");
    setMapType("image");
    setPendingFile(null);
    setEmbedUrl("");
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) resetForm();
        onOpenChange(isOpen);
      }}
    >
      <DialogContent
        className="sm:max-w-lg"
        data-testid="create-camera-plan-dialog"
      >
        <DialogHeader>
          <DialogTitle>New Camera Plan</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="plan-name">Name</Label>
            <Input
              id="plan-name"
              data-testid="camera-plan-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Day 1, Main Stage..."
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="plan-event-date">Event Date</Label>
            <DatePicker
              id="plan-event-date"
              data-testid="camera-plan-event-date-input"
              value={eventDate}
              onChange={setEventDate}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="plan-description">Description</Label>
            <Textarea
              id="plan-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description..."
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label>Type</Label>
            <Select
              value={mapType}
              onValueChange={(v) => setMapType(v as "image" | "embed")}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="image">
                  Image (Floor Plan / Venue Map)
                </SelectItem>
                <SelectItem value="embed">
                  Embed (Figma, Google Maps, ...)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {mapType === "image" && (
            <div className="space-y-2">
              <Label>Floor Plan Image</Label>
              <ImageUpload
                onUpload={(file) => setPendingFile(file)}
                imageUrl={pendingFile ? URL.createObjectURL(pendingFile) : null}
                onRemove={() => setPendingFile(null)}
                className="h-40"
              />
            </div>
          )}

          {mapType === "embed" && (
            <div className="space-y-2">
              <Label htmlFor="embed-url">Embed URL</Label>
              <Input
                id="embed-url"
                value={embedUrl}
                onChange={(e) => setEmbedUrl(e.target.value)}
                placeholder="https://www.figma.com/file/... or Google Maps URL"
              />
              <p className="text-xs text-muted-foreground">
                Paste a Figma share link, Google Maps URL, or any embeddable
                URL. You can also add this later.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            offlineEdit
            onClick={handleSubmit}
            disabled={!name.trim() || isSubmitting}
            data-testid="create-camera-plan-submit"
          >
            {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create Plan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
